import {
  getHttpStatus,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";

type ExperimentAction =
  | "create"
  | "launch"
  | "stop"
  | "resume"
  | "archive"
  | "change_results_goal"
  | "call_decision"
  | "ship_winner"
  | "save_draft"
  | "load_list"
  | "editor_init"
  | "editor_save"
  | "ai_vary_generate"
  | "ai_vary_apply"
  | "generation_job";

type ExperimentEvent =
  | "experiment_created"
  | "experiment_launched"
  | "experiment_stopped"
  | "experiment_resumed"
  | "experiment_archived"
  | "decision_called"
  | "winner_shipped"
  | "variation_generated"
  | "variation_applied"
  | "editor_opened"
  | "draft_saved"
  | "generation_ready";

type ExperimentAttributes = {
  experimentId?: string | null;
  experimentName?: string | null;
  experimentType?: string | null;
  variantId?: string | null;
  draftId?: string | null;
};

const attributesFor = ({
  experimentId,
  experimentName,
  experimentType,
  variantId,
  draftId,
}: ExperimentAttributes = {}) => ({
  experiment_id: experimentId ?? null,
  experiment_name: experimentName ?? null,
  experiment_type: experimentType ?? null,
  variant_id: variantId ?? null,
  draft_id: draftId ?? null,
});

export const logExperimentEvent = (
  event: ExperimentEvent,
  scope: ObservabilityScope,
  attributes?: ExperimentAttributes,
) =>
  logFeatureEvent({
    feature: "experiments",
    event,
    scope,
    attributes: attributesFor(attributes),
  });

/**
 * A 402/403 on an experiment action is the plan gate (expected — Log).
 * Anything else broke the user's workflow and is an Issue.
 */
export const reportExperimentFailure = (
  action: ExperimentAction,
  error: unknown,
  scope: ObservabilityScope,
  attributes?: ExperimentAttributes,
) => {
  const status = getHttpStatus(error);

  if (status === 402 || status === 403) {
    logFeatureEvent({
      feature: "experiments",
      event: "plan_limit_reached",
      level: "warn",
      message: "Experiments: plan gate blocked action",
      scope,
      dedupKey: `plan-limit:${action}:${scope.propertyId ?? "unknown"}`,
      attributes: {
        experiment_action: action,
        http_status: status,
        ...attributesFor(attributes),
      },
    });
    return;
  }

  reportFeatureIssue({
    feature: "experiments",
    error,
    action,
    message: `Experiments: ${action} failed`,
    scope,
    context: attributesFor(attributes),
  });
};

/**
 * Editor/customer-page conditions we expect to hit in the wild (slow or
 * unreachable customer pages, blocked iframes). Notable, but not a defect.
 */
export const logExperimentDegradation = (
  event:
    | "editor_connection_timeout"
    | "editor_connection_lost"
    | "generation_job_failed"
    | "ai_vary_unusable_response",
  scope: ObservabilityScope,
  attributes?: ExperimentAttributes & {
    reason?: string;
    targetUrl?: string;
    /** Origin the parent bridge expected the iframe to post from. */
    expectedOrigin?: string;
    /** Last http(s) origin the bridge rejected — surfaces redirect mismatches. */
    lastRejectedOrigin?: string;
  },
) =>
  logFeatureEvent({
    feature: "experiments",
    event,
    level: "warn",
    scope,
    attributes: {
      reason: attributes?.reason ?? null,
      target_url: attributes?.targetUrl ?? null,
      expected_origin: attributes?.expectedOrigin ?? null,
      last_rejected_origin: attributes?.lastRejectedOrigin ?? null,
      ...attributesFor(attributes),
    },
  });

export const logExperimentListDegraded = (
  scope: ObservabilityScope,
  error: unknown,
) =>
  logFeatureEvent({
    feature: "experiments",
    event: "list_fetch_degraded",
    level: "warn",
    message: "Experiments: property list fetch failed, returning empty",
    scope,
    dedupKey: `list-degraded:${scope.propertyId ?? "unknown"}`,
    attributes: {
      http_status: getHttpStatus(error),
      error_message: error instanceof Error ? error.message : String(error),
    },
  });

/**
 * Behaviour: a test's results goal was changed after launch (or reset to the
 * launch goal, `goal_type: "launch"`). Shows how often goals move post-launch.
 */
export const logResultsGoalChanged = (
  scope: ObservabilityScope,
  attributes: {
    experimentId: string;
    goalType: string;
    clickPreset: string | null;
    clickTargets: number | null;
  },
) =>
  logFeatureEvent({
    feature: "experiments",
    event: "results_goal_changed",
    scope,
    attributes: {
      experiment_id: attributes.experimentId,
      goal_type: attributes.goalType,
      click_preset: attributes.clickPreset,
      click_targets: attributes.clickTargets,
    },
  });

/** Expected friction: someone else changed the results goal at the same time (409). Warn Log, never an Issue. */
export const logResultsGoalConflict = (scope: ObservabilityScope, { experimentId }: { experimentId: string }) =>
  logFeatureEvent({
    feature: "experiments",
    event: "results_goal_conflict",
    level: "warn",
    message: "Experiments: PUT results-goal hit a concurrent change (409)",
    scope,
    attributes: { experiment_id: experimentId },
  });

/**
 * Expected friction: the opt-in `goals=all` request failed (older backend
 * state, or one of its extra queries erred server-side, which the backend
 * reports itself). The page falls back to launch-goal fields, so this is a
 * warn Log, once per session, never an Issue.
 */
export const logExperimentGoalMetricsUnavailable = (
  scope: ObservabilityScope,
  { experimentId, error }: { experimentId: string; error: unknown },
) =>
  logFeatureEvent({
    feature: "experiments",
    event: "goal_metrics_unavailable",
    level: "warn",
    message: "Experiments: goals=all request failed, results fall back to launch-goal fields",
    scope,
    attributes: {
      experiment_id: experimentId,
      http_status: getHttpStatus(error),
      error_message: error instanceof Error ? error.message : null,
    },
    dedupKey: "goal_metrics_unavailable",
  });
